import { useEffect, useRef, useState } from "react"
import { Anchor, AnchorProps, Flex, FlexProps } from "@mantine/core"
import { useTranslation } from "react-i18next"

interface Props extends React.PropsWithChildren, FlexProps {
  onExpandedClick?: () => void
  expanded?: boolean
  innerFlexProps?: FlexProps
  anchorProps?: AnchorProps
  contentHeight?: number
  showMoreLabel?: string
  showLessLabel?: string
}

export function ResponsiveSpoiler({
  children,
  onExpandedClick,
  expanded,
  innerFlexProps,
  anchorProps,
  contentHeight,
  showMoreLabel,
  showLessLabel,
  ...props
}: Props) {
  const [internalExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(true)

  const contentRef = useRef<HTMLDivElement>(null)
  const isExpanded = expanded !== undefined ? expanded : internalExpanded

  const { t } = useTranslation("app_server")

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && contentHeight !== undefined) {
        const { clientHeight } = contentRef.current
        setIsOverflowing(contentHeight > clientHeight)
      }
    }

    checkOverflow()

    window.addEventListener("resize", checkOverflow)
    return () => window.removeEventListener("resize", checkOverflow)
  }, [children, contentHeight])

  return (
    <Flex direction="column" h="0" w="100%" {...props}>
      <Flex
        ref={contentRef}
        style={{
          overflow: "hidden",
          transition: "all 0.3s ease",
        }}
        {...innerFlexProps}
      >
        {children}
      </Flex>
      {isOverflowing && (
        <Anchor
          onClick={(evt) => {
            evt.preventDefault()
            onExpandedClick && onExpandedClick()
            setIsExpanded(!isExpanded)
          }}
          {...anchorProps}
        >
          {isExpanded
            ? showLessLabel || t("hide")
            : showMoreLabel || t("ReadMore")}
        </Anchor>
      )}
    </Flex>
  )
}
